/**
 * Motor de leitura do catálogo estadual de tabelas (Notas, RI, UFESP, ITCMD).
 * Cópia em runtime: react/src/drheranca/data/state_tables.catalog.json
 * (sincronizada a partir do canônico projects/drheranca/tables/state_tables.catalog.json via `npm run sync:state-catalog`).
 */

import rawCatalog from '../data/state_tables.catalog.json';

export interface CatalogRoot {
  $schema_version: string;
  default_state_code: string;
  states: Record<
    string,
    {
      implemented?: boolean;
      references?: {
        table_vigencia_notas?: string;
        table_vigencia_ri?: string;
        fiscal_unit?: { code: string; value_brl: number; value_brl_sample_date?: string };
      };
      cartorio_notas?: {
        escritura_valor_declarado_item_I?: {
          brackets: Array<{ id: string; max_inclusive: number; fee_brl: number }>;
          progressive_after_last_bracket?: {
            threshold_brl: number;
            base_fee_brl: number;
            step_width_brl: number;
            step_fee_brl: number;
            cap_fee_brl: number;
            step_count_mode?: string;
          };
        };
      };
      registro_imoveis?: {
        registro_com_valor_declarado?: {
          rows: Array<{ code: string; min: number; max: number | null; total_brl: number }>;
        };
        prenotacao_exame_calculo_total_brl?: number;
      };
      itcmd?: {
        aliquota_padrao?: number;
        isen_limits_fiscal_units?: {
          causa_mortis_patrimonio_total?: number;
          doacao_valor_transmissao?: number;
        };
      };
    }
  >;
}

const catalog = rawCatalog as CatalogRoot;

function round2(v: number): number {
  return Math.round((v + Number.EPSILON) * 100) / 100;
}

/** Resolve código UF para dados no catálogo (MVP: SP). */
export function getResolvedStateCode(requested?: string | null): string {
  const fallback = catalog.default_state_code || 'SP';
  if (!requested) return fallback;
  const st = catalog.states[requested];
  if (st?.implemented) return requested;
  return fallback;
}

export function getStateTables(stateCode?: string | null) {
  const code = getResolvedStateCode(stateCode ?? undefined);
  const st = catalog.states[code];
  if (!st?.implemented) {
    throw new Error(`UF não implementada no catálogo: ${code}`);
  }
  return st;
}

export interface NotasItemILookupResult {
  feeBrl: number;
  bracketId: string;
  rangeMin: number;
  rangeMax: number | null;
  source: 'zero' | 'bracket' | 'progressive';
}

/**
 * Tabela item I — escritura com valor declarado (CNB/SP), faixas Ia–Id + faixa Ie progressiva.
 */
export function lookupNotasItemI(valorDeclarado: number, stateCode?: string | null): NotasItemILookupResult {
  const Vraw = Number(valorDeclarado) || 0;
  const V = Math.max(0, Vraw);
  const st = getStateTables(stateCode);
  const cfg = st.cartorio_notas?.escritura_valor_declarado_item_I;
  if (!cfg?.brackets?.length) {
    throw new Error('Catálogo sem cartorio_notas.escritura_valor_declarado_item_I');
  }

  if (V < 0.01) {
    return { feeBrl: 0, bracketId: '—', rangeMin: 0, rangeMax: 0, source: 'zero' };
  }

  let prevMax = 0;
  for (let i = 0; i < cfg.brackets.length; i++) {
    const b = cfg.brackets[i];
    const min = i === 0 ? 0.01 : prevMax + 0.01;
    const max = b.max_inclusive;
    prevMax = max;
    if (V >= min && V <= max) {
      return {
        feeBrl: round2(b.fee_brl),
        bracketId: b.id,
        rangeMin: round2(min),
        rangeMax: round2(max),
        source: 'bracket',
      };
    }
  }

  const prog = cfg.progressive_after_last_bracket;
  if (!prog) {
    const last = cfg.brackets[cfg.brackets.length - 1];
    return {
      feeBrl: round2(last.fee_brl),
      bracketId: last.id,
      rangeMin: round2(prevMax + 0.01),
      rangeMax: null,
      source: 'bracket',
    };
  }

  const thr = prog.threshold_brl;
  const excess = Math.max(0, V - thr);
  const steps =
    prog.step_count_mode === 'CEIL'
      ? Math.ceil(excess / prog.step_width_brl)
      : Math.floor(excess / prog.step_width_brl);
  let fee = prog.base_fee_brl + steps * prog.step_fee_brl;
  fee = Math.min(fee, prog.cap_fee_brl);

  return {
    feeBrl: round2(fee),
    bracketId: 'Ie',
    rangeMin: round2(thr + 0.01),
    rangeMax: null,
    source: 'progressive',
  };
}

export interface RiRegistroLookupResult {
  totalBrl: number;
  code: string;
}

/** Tabela II item 1 — registro com valor declarado (RI-SP no catálogo). */
export function lookupRiRegistro(valorDeclarado: number, stateCode?: string | null): RiRegistroLookupResult | null {
  const V = Number(valorDeclarado) || 0;
  if (V < 0.01) return null;
  const st = getStateTables(stateCode);
  const rows = st.registro_imoveis?.registro_com_valor_declarado?.rows;
  if (!rows?.length) return null;

  for (const row of rows) {
    const maxOk = row.max == null ? true : V <= row.max;
    if (V >= row.min && maxOk) {
      return { totalBrl: row.total_brl, code: row.code };
    }
  }
  return null;
}

export function getPrenotacaoValor(stateCode?: string | null): number {
  const st = getStateTables(stateCode);
  const v = st.registro_imoveis?.prenotacao_exame_calculo_total_brl;
  return typeof v === 'number' ? v : 0;
}

export function getUfespRef(stateCode?: string | null): { code: string; valueBrl: number; sampleDate?: string } {
  const st = getStateTables(stateCode);
  const fu = st.references?.fiscal_unit;
  return {
    code: fu?.code ?? 'UFESP',
    valueBrl: fu?.value_brl ?? 0,
    sampleDate: fu?.value_brl_sample_date,
  };
}

export interface ItcmdSettings {
  aliquotaPadrao: number;
  isencaoCausaMortisUf?: number;
  isencaoDoacaoUf?: number;
}

/** Configurações tributárias de ITCMD por UF (MVP: SP). */
export function getItcmdSettings(stateCode?: string | null): ItcmdSettings {
  const st = getStateTables(stateCode);
  return {
    aliquotaPadrao: st.itcmd?.aliquota_padrao ?? 0.04,
    isencaoCausaMortisUf: st.itcmd?.isen_limits_fiscal_units?.causa_mortis_patrimonio_total,
    isencaoDoacaoUf: st.itcmd?.isen_limits_fiscal_units?.doacao_valor_transmissao,
  };
}

/** Identificador legível para relatório (sem path de arquivo). */
export function getTableSetSummary(stateCode?: string | null): string {
  const code = getResolvedStateCode(stateCode ?? undefined);
  const st = getStateTables(code);
  const n = st.references?.table_vigencia_notas;
  const r = st.references?.table_vigencia_ri;
  return `SP — Notas vig. ${n ?? '—'} · RI vig. ${r ?? '—'}`;
}

export interface EmolumentosNotasCatalogFields {
  emolumentosFaixa: string;
  emolumentosFaixaValorInicial: number;
  emolumentosFaixaValorFinal: number;
  emolumentosTotalSemIss: number;
  emolumentosTabeliao?: number;
  emolumentosIss: number;
  emolumentosTotal: number;
  emolumentosPiorCasoTotal?: number;
  emolumentosEconomiaVsPiorCaso?: number;
}

/** Campos de CalculosInventario relacionados a emolumentos de Notas (item I, sem ISS no JSON). */
export function buildEmolumentosNotasFromCatalog(
  montantePartivel: number,
  stateCode?: string | null
): EmolumentosNotasCatalogFields {
  const r = lookupNotasItemI(montantePartivel, stateCode);
  const valorMaxFaixa =
    r.rangeMax != null ? r.rangeMax : Math.max(montantePartivel, r.rangeMin, r.rangeMin + 1);

  return {
    emolumentosFaixa: r.bracketId,
    emolumentosFaixaValorInicial: r.rangeMin,
    emolumentosFaixaValorFinal: valorMaxFaixa,
    emolumentosTotalSemIss: r.feeBrl,
    emolumentosTabeliao: undefined,
    emolumentosIss: 0,
    emolumentosTotal: r.feeBrl,
    emolumentosPiorCasoTotal: undefined,
    emolumentosEconomiaVsPiorCaso: undefined,
  };
}
